import axios from 'axios';
import Auth from '../../auth/auth';

const auth = new Auth();
const headers = () => ({
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'x-access-token': auth.getAccessToken(),
});

const cancelSources = {};

function cancelPrevious(key) {
  // eslint-disable-next-line security/detect-object-injection -- key is a hardcoded string literal at every call site
  if (cancelSources[key]) {
    // eslint-disable-next-line security/detect-object-injection -- same as above
    cancelSources[key].cancel();
  }
  const source = axios.CancelToken.source();
  // eslint-disable-next-line security/detect-object-injection -- same as above
  cancelSources[key] = source;
  return source;
}

export const daActions = {
  LOAD_INSTANCES_REQUEST: 'DA_LOAD_INSTANCES_REQUEST',
  LOAD_INSTANCES_SUCCESS: 'DA_LOAD_INSTANCES_SUCCESS',
  LOAD_INSTANCES_FAILURE: 'DA_LOAD_INSTANCES_FAILURE',

  LOAD_DD_SCHEMAS_REQUEST: 'DA_LOAD_DD_SCHEMAS_REQUEST',
  LOAD_DD_SCHEMAS_SUCCESS: 'DA_LOAD_DD_SCHEMAS_SUCCESS',
  LOAD_DD_SCHEMAS_FAILURE: 'DA_LOAD_DD_SCHEMAS_FAILURE',

  LOAD_GRAPH_REQUEST: 'DA_LOAD_GRAPH_REQUEST',
  LOAD_GRAPH_SUCCESS: 'DA_LOAD_GRAPH_SUCCESS',
  LOAD_GRAPH_FAILURE: 'DA_LOAD_GRAPH_FAILURE',

  LOAD_IMPACT_REQUEST: 'DA_LOAD_IMPACT_REQUEST',
  LOAD_IMPACT_SUCCESS: 'DA_LOAD_IMPACT_SUCCESS',
  LOAD_IMPACT_FAILURE: 'DA_LOAD_IMPACT_FAILURE',

  LOAD_DDL_REQUEST: 'DA_LOAD_DDL_REQUEST',
  LOAD_DDL_SUCCESS: 'DA_LOAD_DDL_SUCCESS',
  LOAD_DDL_FAILURE: 'DA_LOAD_DDL_FAILURE',

  LOAD_COLUMNS_REQUEST: 'DA_LOAD_COLUMNS_REQUEST',
  LOAD_COLUMNS_SUCCESS: 'DA_LOAD_COLUMNS_SUCCESS',
  LOAD_COLUMNS_FAILURE: 'DA_LOAD_COLUMNS_FAILURE',

  REFRESH_REQUEST: 'DA_REFRESH_REQUEST',
  REFRESH_SUCCESS: 'DA_REFRESH_SUCCESS',
  REFRESH_FAILURE: 'DA_REFRESH_FAILURE',
};

export function loadInstances() {
  return function(dispatch) {
    const source = cancelPrevious('da_loadInstances');
    dispatch({type: daActions.LOAD_INSTANCES_REQUEST});
    return axios.get(
        `${process.env.REACT_APP_API_URL}/dataDictionary/instances`,
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.LOAD_INSTANCES_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.LOAD_INSTANCES_FAILURE, payload: error, error: true});
        });
  };
}

// Schemas belong to a specific DD instance (dd_schema_registry.instance_id), so we use
// the per-instance endpoint rather than the global list — a schema list is only meaningful
// in the context of the instance selected above.
export function loadSchemasForInstance(instanceId) {
  return function(dispatch) {
    const source = cancelPrevious('da_loadDdSchemas');
    dispatch({type: daActions.LOAD_DD_SCHEMAS_REQUEST});
    if (!instanceId) {
      dispatch({type: daActions.LOAD_DD_SCHEMAS_SUCCESS, payload: {response: []}});
      return Promise.resolve();
    }
    return axios.get(
        `${process.env.REACT_APP_API_URL}/dataDictionary/schemas/${encodeURIComponent(instanceId)}`,
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.LOAD_DD_SCHEMAS_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.LOAD_DD_SCHEMAS_FAILURE, payload: error, error: true});
        });
  };
}

export function loadGraph(params) {
  return function(dispatch) {
    const source = cancelPrevious('da_loadGraph');
    dispatch({type: daActions.LOAD_GRAPH_REQUEST});
    const qp = new URLSearchParams();
    const put = (k, v) => {
      if (v !== undefined && v !== null && v !== '') qp.append(k, v);
    };
    put('schemaId', params.schemaId);
    put('objectId', params.objectId);
    put('direction', params.direction);
    put('depth', params.depth);
    put('includeColumns', params.includeColumns);
    return axios.get(
        `${process.env.REACT_APP_API_URL}/referentialIntegrity/graph?${qp.toString()}`,
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.LOAD_GRAPH_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.LOAD_GRAPH_FAILURE, payload: error, error: true});
        });
  };
}

export function loadImpact(params) {
  return function(dispatch) {
    const source = cancelPrevious('da_loadImpact');
    dispatch({type: daActions.LOAD_IMPACT_REQUEST});
    const qp = new URLSearchParams();
    const put = (k, v) => {
      if (v !== undefined && v !== null && v !== '') qp.append(k, v);
    };
    put('schemaId', params.schemaId);
    put('objectId', params.objectId);
    put('changeType', params.changeType);
    put('columnId', params.columnId);
    return axios.get(
        `${process.env.REACT_APP_API_URL}/referentialIntegrity/impact?${qp.toString()}`,
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.LOAD_IMPACT_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.LOAD_IMPACT_FAILURE, payload: error, error: true});
        });
  };
}

export function loadDdl(params) {
  return function(dispatch) {
    const source = cancelPrevious('da_loadDdl');
    dispatch({type: daActions.LOAD_DDL_REQUEST});
    const qp = new URLSearchParams();
    const put = (k, v) => {
      if (v !== undefined && v !== null && v !== '') qp.append(k, v);
    };
    put('schemaId', params.schemaId);
    put('objectId', params.objectId);
    put('changeType', params.changeType);
    put('columnId', params.columnId);
    put('dialect', params.dialect);
    return axios.get(
        `${process.env.REACT_APP_API_URL}/referentialIntegrity/ddl?${qp.toString()}`,
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.LOAD_DDL_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.LOAD_DDL_FAILURE, payload: error, error: true});
        });
  };
}

export function loadColumns(objectIds) {
  return function(dispatch) {
    const source = cancelPrevious('da_loadColumns');
    dispatch({type: daActions.LOAD_COLUMNS_REQUEST});
    const csv = Array.isArray(objectIds) ? objectIds.join(',') : objectIds;
    return axios.get(
        `${process.env.REACT_APP_API_URL}/referentialIntegrity/columns?objectIds=${encodeURIComponent(csv)}`,
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.LOAD_COLUMNS_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.LOAD_COLUMNS_FAILURE, payload: error, error: true});
        });
  };
}

export function refreshRelationships(schemaId) {
  return function(dispatch) {
    const source = cancelPrevious('da_refresh');
    dispatch({type: daActions.REFRESH_REQUEST});
    return axios.post(
        `${process.env.REACT_APP_API_URL}/referentialIntegrity/refresh`,
        {schemaId},
        {headers: headers(), cancelToken: source.token},
    )
        .then((response) => response.data)
        .then((json) => dispatch({type: daActions.REFRESH_SUCCESS, payload: json}))
        .catch((error) => {
          if (axios.isCancel(error)) return;
          dispatch({type: daActions.REFRESH_FAILURE, payload: error, error: true});
        });
  };
}
